import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { sfqApi } from "../../../api/feasibility";
import {
  fetchNarrativeTemplates,
  fetchQuestionTemplates,
  fetchPriorVersions,
} from "../Redux/feasibility";
import { NarrativeView, QuestionsView } from "../FeasibilityEditor/VersionContent";
import { toEditorRows } from "../QuestionRowsEditor";
import {
  Backdrop, Modal, Header, Title, CloseBtn, Body, Footer, Btn, Loading,
} from "../SiteInvitationList/emailModalUI";

// One picker for both the narrative and the question editors. It offers the same three
// groups — the org's default templates, the user's own, and every published version of
// their own studies — plus starting from nothing, and hands the chosen content back to
// the caller to overwrite with or append to what they are editing.
//
//   <TemplatePicker kind="narrative" | "questions"
//                   hasContent={bool}                 // guards the overwrite confirm
//                   onApply={(payload, mode) => …}    // payload: { html } | { questions }
//                                                     // mode: "overwrite" | "append"
//                   onClose={() => …} />

const Split = styled.div`
  display: flex;
  gap: 0;
  flex: 1;
  min-height: 0;
  margin: -14px -20px;
`;

const Rail = styled.div`
  width: 260px;
  flex-shrink: 0;
  border-right: 1px solid #dee2e6;
  overflow-y: auto;
  padding: 12px 0;
  background: #f8f9fa;
`;

const GroupLabel = styled.div`
  padding: 10px 16px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
`;

const Item = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 7px 16px;
  border: none;
  border-left: 3px solid ${(p) => (p.$active ? "#0d6efd" : "transparent")};
  background: ${(p) => (p.$active ? "#e7f1ff" : "transparent")};
  color: ${(p) => (p.$active ? "#0d6efd" : "#495057")};
  font-size: 13px;
  cursor: pointer;
  &:hover { background: ${(p) => (p.$active ? "#e7f1ff" : "#eef0f2")}; }
`;

// 템플릿 이름·스터디 제목이 길면 좁은 레일을 그대로 뚫고 나간다. 이름은 두 줄까지만
// 보이고 나머지는 접고(공백 없는 긴 이름도 접히도록 overflow-wrap), 메타 한 줄은 …로
// 자른다. 잘린 값은 Item의 title 툴팁으로 전체를 확인할 수 있다.
const ItemName = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
`;

const ItemMeta = styled.div`
  font-size: 11px;
  color: #adb5bd;
  margin-top: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyGroup = styled.div`
  padding: 4px 16px 6px;
  font-size: 12px;
  color: #adb5bd;
`;

const Preview = styled.div`
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 14px 20px;
`;

const PreviewHint = styled.div`
  padding: 40px 0;
  text-align: center;
  color: #adb5bd;
  font-size: 13px;
`;

const BLANK = { kind: "blank" };

export default function TemplatePicker({ kind, hasContent, onApply, onClose }) {
  const dispatch = useDispatch();
  const isNarrative = kind === "narrative";

  const templates = useSelector((s) =>
    isNarrative ? s.feasibility.narrativeTemplates : s.feasibility.questionTemplates
  );
  const listLoading = useSelector((s) =>
    isNarrative ? s.feasibility.ntLoading : s.feasibility.qtLoading
  );
  const priorVersions = useSelector((s) => s.feasibility.priorVersions);
  const priorLoading  = useSelector((s) => s.feasibility.priorVersionsLoading);

  // `selected` is what the rail highlights; `content` is the body fetched for it.
  const [selected, setSelected] = useState(BLANK);
  const [content, setContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    dispatch(isNarrative ? fetchNarrativeTemplates() : fetchQuestionTemplates());
    dispatch(fetchPriorVersions());
  }, [dispatch, isNarrative]);

  // Bodies are fetched on selection rather than cached in Redux — a template the user
  // just edited in another tab should come back fresh.
  useEffect(() => {
    if (selected.kind === "blank") {
      setContent(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setContentLoading(true);
    setError(null);

    const request = selected.kind === "prior"
      ? sfqApi.getPriorVersion(selected.id)
      : isNarrative
        ? sfqApi.getNarrativeTemplate(selected.id)
        : sfqApi.getQuestionTemplate(selected.id);

    request
      .then((res) => {
        if (cancelled) return;
        const d = res.data.data;
        setContent(
          selected.kind === "prior"
            ? { html: d.narrativeHtml || "", questions: d.questions || [] }
            : isNarrative
              ? { html: d.html || "", questions: [] }
              : { html: "", questions: d.questions || [] }
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.error?.message || err?.message || String(err));
        setContent(null);
      })
      .finally(() => { if (!cancelled) setContentLoading(false); });

    return () => { cancelled = true; };
  }, [selected, isNarrative]);

  const defaults = useMemo(() => templates.filter((t) => t.scope === "default"), [templates]);
  const mine     = useMemo(() => templates.filter((t) => t.scope === "user"), [templates]);

  const isBlank = selected.kind === "blank";
  const ready   = isBlank || (!!content && !contentLoading && !error);

  function apply(mode) {
    if (mode === "overwrite" && hasContent) {
      const what = isNarrative ? "작성 중인 Narrative" : "작성 중인 문항";
      if (!window.confirm(`${what}을(를) 모두 덮어씁니다. 계속하시겠습니까?`)) return;
    }
    if (isBlank) {
      onApply(isNarrative ? { html: "" } : { questions: [] }, "overwrite");
    } else {
      onApply(
        isNarrative
          ? { html: content.html }
          : { questions: toEditorRows(content.questions) },
        mode
      );
    }
    onClose();
  }

  const title = isNarrative ? "Narrative 불러오기" : "문항 불러오기";

  return (
    <Backdrop onClick={onClose}>
      <Modal $wide onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{title}</Title>
          <CloseBtn onClick={onClose}>×</CloseBtn>
        </Header>

        <Body>
          <Split>
            <Rail>
              <Item $active={isBlank} onClick={() => setSelected(BLANK)}>
                <ItemName>빈 문서로 시작</ItemName>
                <ItemMeta>아무것도 불러오지 않습니다</ItemMeta>
              </Item>

              <GroupLabel>기본 템플릿</GroupLabel>
              {listLoading && !templates.length ? (
                <EmptyGroup>불러오는 중…</EmptyGroup>
              ) : defaults.length ? (
                defaults.map((t) => (
                  <Item
                    key={`d${t.id}`}
                    $active={selected.kind === "template" && selected.id === t.id}
                    onClick={() => setSelected({ kind: "template", id: t.id })}
                    title={t.name}
                  >
                    <ItemName>{t.name}</ItemName>
                    {!isNarrative && <ItemMeta>{t.questionCount}개 문항</ItemMeta>}
                  </Item>
                ))
              ) : (
                <EmptyGroup>없음</EmptyGroup>
              )}

              <GroupLabel>내 템플릿</GroupLabel>
              {mine.length ? (
                mine.map((t) => (
                  <Item
                    key={`u${t.id}`}
                    $active={selected.kind === "template" && selected.id === t.id}
                    onClick={() => setSelected({ kind: "template", id: t.id })}
                    title={t.name}
                  >
                    <ItemName>{t.name}</ItemName>
                    {!isNarrative && <ItemMeta>{t.questionCount}개 문항</ItemMeta>}
                  </Item>
                ))
              ) : (
                <EmptyGroup>없음</EmptyGroup>
              )}

              <GroupLabel>이전 작성본</GroupLabel>
              {priorLoading && !priorVersions.length ? (
                <EmptyGroup>불러오는 중…</EmptyGroup>
              ) : priorVersions.length ? (
                priorVersions.map((v) => (
                  <Item
                    key={`p${v.id}`}
                    $active={selected.kind === "prior" && selected.id === v.id}
                    onClick={() => setSelected({ kind: "prior", id: v.id })}
                    title={`${v.studyCode} · v${v.versionNo} — ${v.studyTitle}`}
                  >
                    <ItemName>{v.studyCode} · v{v.versionNo}</ItemName>
                    <ItemMeta>
                      {v.studyTitle} · {new Date(v.createdAt).toLocaleDateString()}
                    </ItemMeta>
                  </Item>
                ))
              ) : (
                <EmptyGroup>발행된 버전이 없습니다</EmptyGroup>
              )}
            </Rail>

            <Preview>
              {isBlank ? (
                <PreviewHint>빈 문서로 시작합니다.</PreviewHint>
              ) : contentLoading ? (
                <Loading>Loading...</Loading>
              ) : error ? (
                <PreviewHint style={{ color: "#dc3545" }}>{error}</PreviewHint>
              ) : isNarrative ? (
                <NarrativeView html={content?.html} />
              ) : (
                <QuestionsView questions={content?.questions} />
              )}
            </Preview>
          </Split>
        </Body>

        <Footer>
          <Btn onClick={onClose}>취소</Btn>
          {/* Appending nothing to something is a no-op, so blank only offers overwrite. */}
          <Btn disabled={!ready || isBlank} onClick={() => apply("append")}>
            뒤에 추가
          </Btn>
          <Btn $primary disabled={!ready} onClick={() => apply("overwrite")}>
            덮어쓰기
          </Btn>
        </Footer>
      </Modal>
    </Backdrop>
  );
}
